import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Array of video URLs from Google Cloud Storage
const videoUrls = [
  'https://storage.googleapis.com/saved_media_assets/Social%20Proof/sp-vid-7.webm',
  'https://storage.googleapis.com/saved_media_assets/Social%20Proof/sp-vid-6.webm',
  'https://storage.googleapis.com/saved_media_assets/Social%20Proof/sp-vid-1.webm',
  'https://storage.googleapis.com/saved_media_assets/Social%20Proof/sp-vid-5.webm',
  'https://storage.googleapis.com/saved_media_assets/Social%20Proof/sp-vid-3.webm',
  'https://storage.googleapis.com/saved_media_assets/Social%20Proof/sp-vid-8.webm',
  'https://storage.googleapis.com/saved_media_assets/Social%20Proof/sp-vid-2.webm',
  'https://storage.googleapis.com/saved_media_assets/Social%20Proof/sp-vid-4.webm',
];

const SocialProof = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true); // Initially true assuming content overflows

  // No longer need getEmbedUrl

  const checkScrollability = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      // Check if scrolled to the very end (within a small tolerance)
      setCanScrollRight(container.scrollLeft < container.scrollWidth - container.clientWidth - 1);
    }
  };

  // Attach scroll listener and check initial state
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      // Check initial scrollability
      checkScrollability();

      container.addEventListener('scroll', checkScrollability);
      // Also check on resize in case container size changes
      window.addEventListener('resize', checkScrollability);

      return () => {
        if (container) { // Check if container still exists on cleanup
           container.removeEventListener('scroll', checkScrollability);
        }
        window.removeEventListener('resize', checkScrollability);
      };
    }
  }, []); // Empty dependency array ensures this runs only once on mount and cleanup on unmount

  // Scroll Amount - adjust as needed
  const scrollAmount = 300;

  const handleScrollLeft = () => {
    scrollContainerRef.current?.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
  };

  const handleScrollRight = () => {
    scrollContainerRef.current?.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  };


  return (
    // Add relative positioning to the main section for arrow positioning
    <section className="relative w-full py-8">
      <div className="">
        {/* Scrollable container */}
        <div
          ref={scrollContainerRef}
          className="flex overflow-x-auto space-x-4 sm:space-x-6 pb-4 no-scrollbar" // Add 'no-scrollbar' class if you have it defined globally
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }} // Inline styles to hide scrollbar
        >
          {videoUrls.map((videoUrl, index) => (
            <div
              key={index}
              // Adjusted width slightly for potentially better fit with scrolling
              className="relative flex-shrink-0 w-48 sm:w-64 rounded-lg overflow-hidden bg-[#0b0b0c] border border-white/10 shadow-lg"
            >
              {/* Aspect ratio div - Keeps the container size consistent */}
              <div className="pt-[177.77%]" />

              {/* Video Element */}
              <video
                src={videoUrl}
                // Muted is often required for autoplay to work in modern browsers
                muted
                // Start playing automatically
                autoPlay
                // Loop the video
                loop
                // Important for iOS to play inline
                playsInline
                // Hide default browser controls
                controls={false}
                title={`Social Proof Video ${index + 1}`}
                className="absolute inset-0 w-full h-full border-0 object-cover" // Use object-cover to fill the area
                // Consider adding preload="metadata" or "auto" if needed
                // preload="metadata"
              />

              {/* Transparent overlay - Keeps the existing behavior of preventing direct interaction/hover effects on the video */}
              <div className="absolute inset-0 w-full h-full bg-transparent pointer-events-none"></div>
            </div>
          ))}
        </div>

        {/* Left Arrow */}
        {canScrollLeft && (
          <button
            onClick={handleScrollLeft}
            className="absolute left-2 top-1/2 transform -translate-y-1/2 z-10 p-2 bg-white rounded-sm text-black hover:bg-[#dddddd] focus:outline-none transition-opacity duration-300 ml-2 shadow-md "
            aria-label="Scroll left"
          >
            <ChevronLeft size={24} />
          </button>
        )}

        {/* Right Arrow */}
        {canScrollRight && (
          <button
            onClick={handleScrollRight}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 z-10 p-2 bg-white rounded-sm text-black hover:bg-[#dddddd] focus:outline-none transition-opacity duration-300 mr-2 shadow-md"
            aria-label="Scroll right"
          >
            <ChevronRight size={24} />
          </button>
        )}
      </div>
    </section>
  );
};

export default SocialProof;

// Ensure you have Tailwind configured or add this utility class to your global CSS:
/*
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;  // IE 10+
  scrollbar-width: none;  // Firefox
}
*/